import { nanoid } from '@reduxjs/toolkit';
import axios, { AxiosError, AxiosRequestConfig, AxiosResponse } from 'axios';
import { ApiResponse } from '@types';
import { auth, config as configuration } from '../config';
import { v4 } from 'uuid';
import { setAdditionalProperties } from 'fmc-digital-common';
import { logApiEvent } from '@analytics/amplifyLogging';

export const instance = axios.create();
//instance for tier 1 application
export const instanceTier1 = axios.create();
export const selfServInstance = axios.create();
let store: any;

export const injectStore = (_store: any) => {
    store = _store;
};

export async function axiosApi(config: AxiosRequestConfig): Promise<ApiResponse> {
    return instance(config)
        .then((response: AxiosResponse) => ({ response: response.data.responseDetails || response.data, error: undefined }))
        .catch((error: AxiosError) => {
            const {
                message,
                response: { status, data }
            } = error;
            return { response: undefined, error: { id: nanoid(16), message, status, data } };
        });
}
export async function axiosT1Api(config: AxiosRequestConfig): Promise<ApiResponse> {
    return instance(config)
        .then((response: AxiosResponse) => ({ response: response.data, error: undefined }))
        .catch((error: AxiosError) => {
            const {
                message,
                response: { status, data }
            } = error;
            return { response: undefined, error: { id: nanoid(16), message, status, data } };
        });
}
export async function axiosApi2(config: AxiosRequestConfig): Promise<ApiResponse> {
    return await instance(config);
}

// Add a request interceptor
instance.interceptors.request.use(
    function (config: any) {
        const token = auth.isNotDefined ? null : auth.getOkta().getAccessToken();
        setAdditionalProperties('authToken', token);
        const correlationId = v4();

        let reqUrl: string = config.url;
        reqUrl = reqUrl.indexOf('?') > 0 ? reqUrl.substring(0, reqUrl.indexOf('?')) : reqUrl;

        config.headers = config.url.includes('gateway')
            ? {
                  authorization: `Bearer ${token}`,
                  startTime: new Date().getTime(),
                  'x-api-key': `${configuration.env('xApiKey')}`,
                  ...config.headers
              }
            : {
                  authorization: `Bearer ${token}`,
                  channel: 'servicing',
                  division: 'digital_originations',
                  application: 'auth_customer_portal',
                  siteId: '02',
                  'request-startTime': new Date().getTime(),
                  ...config.headers
              };

        logApiEvent({
            description: `APIRequest`,
            type: 'API',
            action: 'APIRequest',
            request: reqUrl,
            correlationId: correlationId.toString()
        });
        config.headers['x-correlation-id'] = correlationId;
        return config;
    },
    function (error) {
        // Do something with request error
        let errReqUrl = `${error.config.baseURL}${error.config.url}`;
        errReqUrl = errReqUrl.indexOf('?') > 0 ? errReqUrl.substring(0, errReqUrl.indexOf('?')) : errReqUrl;
        logApiEvent({
            description: `APIResponse - ${error?.message}`,
            action: 'APIResponse',
            type: 'API',
            success: 'false',
            request: errReqUrl,
            error: `${error.response?.status} - ${error.message}`,
            correlationId: error.config.headers['x-correlation-id']
        });

        return Promise.reject(error);
    }
);

// Tier 1 app request interceptor
instanceTier1.interceptors.request.use(
    function (config: any) {
        const token = auth.isNotDefined ? null : auth.getOkta().getAccessToken();
        setAdditionalProperties('authToken', token);
        const correlationId = v4();

        let reqUrl: string = config.url;
        reqUrl = reqUrl.indexOf('?') > 0 ? reqUrl.substring(0, reqUrl.indexOf('?')) : reqUrl;

        config.headers = {
            authorization: `Bearer ${token}`,
            startTime: new Date().getTime(),
            'x-api-key': `${configuration.env('xApiKey')}`,
            ...config.headers
        };

        logApiEvent({
            description: `APIRequest`,
            type: 'API',
            action: 'APIRequest',
            request: reqUrl,
            correlationId: correlationId.toString()
        });
        config.headers['x-correlation-id'] = correlationId;
        return config;
    },
    function (error) {
        // Do something with request error
        let errReqUrl = `${error.config.baseURL}${error.config.url}`;
        errReqUrl = errReqUrl.indexOf('?') > 0 ? errReqUrl.substring(0, errReqUrl.indexOf('?')) : errReqUrl;
        logApiEvent({
            description: `APIResponse - ${error?.message}`,
            action: 'APIResponse',
            type: 'API',
            success: 'false',
            request: errReqUrl,
            error: `${error.response?.status} - ${error.message}`,
            correlationId: error.config.headers['x-correlation-id']
        });

        return Promise.reject(error);
    }
);
interface TError {
    errorDetails: Array<{ code: string; message: string; reason: string }>;
    success: boolean;
}

// Add a response interceptor
instance.interceptors.response.use(
    (response: AxiosResponse) => {
        const { url } = response.config;
        const { success, correlationId } = response.data;
        let requestStartTime = response.config.headers['request-startTime'];
        if (!requestStartTime) requestStartTime = response.config.headers['startTime'];

        logApiEvent({
            request: url,
            description: `APIResponse - ${success ? JSON.stringify(success) : true}`,
            action: 'APIResponse',
            type: 'API',
            success: `${success ? success : true}`,
            correlationId,
            responseTime: (new Date().getTime() - requestStartTime).toString()
        });

        return response;
    },
    function (error: AxiosError<TError, any>) {
        const { config, data } = error.response;
        const { errorDetails, success } = data;
        const { code, message, reason } =
            errorDetails?.length > 0 ? errorDetails[0] : { code: 500, message: error?.message, reason: 'Server error' };
        logApiEvent({
            description: `APIResponse - ${success ? JSON.stringify(success) : false}`,
            request: config.url,
            type: 'API',
            action: 'APIResponse',
            success: `${success ? success : false}`,
            correlationId: error.config.headers['x-correlation-id'],
            error: `${code} - ${message} - ${reason}`
        });
        console.log('interceptor error response');
        return Promise.reject(error);
    }
);

// Tier 1 app response interceptor
instanceTier1.interceptors.response.use(
    (response: AxiosResponse) => {
        const { url } = response.config;
        const { success, correlationId } = response.data;
        let requestStartTime = response.config.headers['request-startTime'];
        if (!requestStartTime) requestStartTime = response.config.headers['startTime'];

        logApiEvent({
            request: url,
            description: `APIResponse - ${success ? JSON.stringify(success) : true}`,
            action: 'APIResponse',
            type: 'API',
            success: `${success ? success : true}`,
            correlationId,
            responseTime: (new Date().getTime() - requestStartTime).toString()
        });

        return response;
    },
    function (error: AxiosError<TError, any>) {
        const { config, data } = error.response;
        const { errorDetails, success } = data;
        const { code, message, reason } =
            errorDetails?.length > 0 ? errorDetails[0] : { code: 500, message: error?.message, reason: 'Server error' };
        logApiEvent({
            description: `APIResponse - ${success ? JSON.stringify(success) : false}`,
            request: config.url,
            type: 'API',
            action: 'APIResponse',
            success: `${success ? success : false}`,
            correlationId: error.config.headers['x-correlation-id'],
            error: `${code} - ${message} - ${reason}`
        });
        console.log('interceptor error response');
        return Promise.reject(error);
    }
);
